import { db } from '../../firebase/config';
import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { User } from '../../types';
import { isTargetViewerRole } from '../../utils/authRoles';

export type AnalyticsEventType = 'page_view' | 'form_edit' | 'form_save' | 'form_submit' | 'error';

export interface SessionEvent {
  type: AnalyticsEventType;
  timestamp?: any;
  clientTimestamp: number;
  pageId?: string;
  data?: Record<string, any>;
}

export interface SessionSummary {
  pageViews: number;
  totalTime: number; // seconds
  conversions: number;
}

export interface AnalyticsSessionDoc {
  userId: string;
  clientId?: string;
  startedAt: any;
  lastActiveAt: any;
  device?: string;
  userAgent?: string;
  events: SessionEvent[];
  summary: SessionSummary;
}

const SESSION_STORAGE_KEY = 'voluntas.analytics.session';
const FLUSH_INTERVAL_MS = 30_000;
const FLUSH_EVENT_THRESHOLD = 10;

function getDeviceDescriptor(): string {
  try {
    const width = window.innerWidth;
    const height = window.innerHeight;
    return `${width}x${height}`;
  } catch {
    return 'unknown';
  }
}

function genSessionId(): string {
  try {
    // Prefer native crypto.randomUUID when available
    if (typeof (globalThis as any).crypto !== 'undefined' && typeof (globalThis as any).crypto.randomUUID === 'function') {
      return (globalThis as any).crypto.randomUUID();
    }
  } catch {}
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

interface PersistedSessionInfo {
  sessionId: string;
  userId: string;
  createdAt: number;
}

function readPersistedSession(): PersistedSessionInfo | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePersistedSession(info: PersistedSessionInfo) {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(info));
  } catch {}
}

function clearPersistedSession() {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {}
}

class SessionAnalyticsManager {
  private static instance: SessionAnalyticsManager | null = null;
  private sessionId: string | null = null;
  private buffer: SessionEvent[] = [];
  private lastFlushAt = 0;
  private flushTimer: any = null;
  private lastActiveAtMs = Date.now();

  static getInstance(): SessionAnalyticsManager {
    if (!SessionAnalyticsManager.instance) {
      SessionAnalyticsManager.instance = new SessionAnalyticsManager();
    }
    return SessionAnalyticsManager.instance;
  }

  async ensureSession(user: User | null | undefined): Promise<string | null> {
    if (!user) {
      // No authenticated user; do not track
      return null;
    }

    if (isTargetViewerRole(user.role)) {
      return null;
    }

    const persisted = readPersistedSession();
    if (persisted && persisted.userId === user.uid) {
      this.sessionId = persisted.sessionId;
      return this.sessionId;
    }

    const newSessionId = genSessionId();
    const sessionRef = doc(db, 'analytics_sessions', newSessionId);
    const clientIdVal = (user as any).clientId ?? (user as any).client_id ?? (user as any).primary_application_id ?? (user as any).linkedApplicationId ?? null;
    const base: AnalyticsSessionDoc = {
      userId: user.uid,
      startedAt: serverTimestamp(),
      lastActiveAt: serverTimestamp(),
      device: getDeviceDescriptor(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      events: [],
      summary: { pageViews: 0, totalTime: 0, conversions: 0 },
      ...(clientIdVal !== null ? { clientId: clientIdVal } : {}),
    } as any;

    await setDoc(sessionRef, base, { merge: false });

    this.sessionId = newSessionId;
    writePersistedSession({ sessionId: newSessionId, userId: user.uid, createdAt: Date.now() });
    this.scheduleFlush();
    return this.sessionId;
  }

  private scheduleFlush() {
    if (this.flushTimer) return;
    this.flushTimer = setInterval(() => {
      this.flush().catch(() => {});
    }, FLUSH_INTERVAL_MS);
  }

  async track(user: User | null | undefined, type: AnalyticsEventType, payload?: { pageId?: string; data?: Record<string, any> }) {
    const sessionId = await this.ensureSession(user);
    if (!sessionId) return;

    const event: SessionEvent = {
      type,
      timestamp: serverTimestamp(),
      clientTimestamp: Date.now(),
      pageId: payload?.pageId,
      data: {
        ...payload?.data,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      },
    };

    this.buffer.push(event);
    this.lastActiveAtMs = Date.now();
    if (this.buffer.length >= FLUSH_EVENT_THRESHOLD) {
      await this.flush();
    }
  }

  async flush() {
    if (!this.sessionId || this.buffer.length === 0) return;
    const now = Date.now();
    const eventsToSend = this.buffer.splice(0, this.buffer.length);
    const pageViewsToAdd = eventsToSend.filter(e => e.type === 'page_view').length;
    const conversionsToAdd = eventsToSend.filter(e => e.type === 'form_submit').length;
    const secondsSinceLast = Math.max(0, Math.round((now - this.lastActiveAtMs) / 1000));

    try {
      const ref = doc(db, 'analytics_sessions', this.sessionId);
      await updateDoc(ref, {
        events: arrayUnion(...eventsToSend),
        lastActiveAt: serverTimestamp(),
        'summary.pageViews': (await this.getFieldExisting<number>('summary.pageViews')) + pageViewsToAdd,
        'summary.conversions': (await this.getFieldExisting<number>('summary.conversions')) + conversionsToAdd,
        'summary.totalTime': (await this.getFieldExisting<number>('summary.totalTime')) + secondsSinceLast,
      } as any);
      this.lastFlushAt = now;
    } catch (err) {
      // On failure, re-queue events at the front
      this.buffer = [...eventsToSend, ...this.buffer];
    }
  }

  private async getFieldExisting<T>(path: string): Promise<T> {
    // Fetch document once to obtain current summary fields if needed
    try {
      const ref = doc(db, 'analytics_sessions', this.sessionId!);
      const snap = await getDoc(ref);
      const data = snap.data() as any;
      const segments = path.split('.');
      let cursor: any = data;
      for (const s of segments) cursor = cursor?.[s];
      return (cursor as T) ?? (0 as any);
    } catch {
      return 0 as any;
    }
  }

  endSession() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.sessionId = null;
    clearPersistedSession();
    this.buffer = [];
  }
}

export const sessionAnalytics = SessionAnalyticsManager.getInstance();


